/**
 * Wave 6 S3 -- IdleTimerProvider.
 *
 * Wraps the app body. Fetches the tenant's session timeout config, mounts
 * useIdleTimer with that idle window, and shows IdleWarningModal when the
 * threshold is near. On real idle, calls the auth store logout.
 *
 * No-op for unauthenticated views (the hook is disabled until a token
 * exists).
 */
import { useEffect, useRef, useState } from 'react'
import { useIdleTimer } from '@/hooks/useIdleTimer'
import IdleWarningModal from './IdleWarningModal'
import ConnectionLostBanner from './ConnectionLostBanner'
import {
  sessionManagementApi,
  type TimeoutConfig,
} from '@/api/sessionManagement'
import { getAccessToken, clearAllAuth } from '@/lib/tokenStorage'
// SESSION-LOGOUT-WHILE-POLLING-01 / H1 -- successful polls of a background job
// the user started. See lib/backgroundActivity.ts for why this is a narrower
// signal than "any HTTP traffic".
import { subscribeBackgroundActivity } from '@/lib/backgroundActivity'
// The idle logout clears storage directly rather than through the store, so
// nothing would otherwise tell an in-flight permissions/identity fetch that the
// session is over -- and a late write would re-persist the departed user's set
// into `auth-storage` just before we navigate away, for the next person on this
// browser to inherit (review #1081 R4).
import { publishAuthCleared } from '@/lib/authTokenSync'

interface IdleTimerProviderProps {
  children: React.ReactNode
}

function hasAuthToken(): boolean {
  if (typeof window === 'undefined') return false
  return !!getAccessToken()
}

export default function IdleTimerProvider(props: IdleTimerProviderProps) {
  const { children } = props
  const [config, setConfig] = useState<TimeoutConfig | null>(null)
  const [warningOpen, setWarningOpen] = useState(false)
  const [authed, setAuthed] = useState<boolean>(hasAuthToken())

  useEffect(() => {
    let cancelled = false
    const tick = () => {
      const ok = hasAuthToken()
      if (!cancelled) setAuthed(ok)
    }
    // Re-check periodically (logout in another tab, etc.)
    const handle = setInterval(tick, 5000)
    return () => {
      cancelled = true
      clearInterval(handle)
    }
  }, [])

  useEffect(() => {
    if (!authed) {
      setConfig(null)
      return
    }
    let cancelled = false
    sessionManagementApi
      .getTimeoutConfig()
      .then((c) => {
        if (!cancelled) setConfig(c)
      })
      .catch(() => {
        // Stay silent -- defaults will apply via the hook.
      })
    return () => {
      cancelled = true
    }
  }, [authed])

  const idleMinutes = config?.idle_timeout_minutes ?? 15

  const { isWarning, msUntilIdle, reset } = useIdleTimer({
    idleMinutes,
    warnMinutes: 1,
    enabled: authed,
    onWarning: () => setWarningOpen(true),
    onIdle: () => {
      // Force-logout: clear token + redirect. The auth store's interceptor
      // handles the 401 path, so we just nuke the token here.
      try {
        clearAllAuth()
        publishAuthCleared()
        window.location.href = '/login?reason=idle'
      } catch {
        // SSR / privacy mode -- nothing to do.
      }
    },
  })

  // Sync modal state with the hook's warning flag.
  useEffect(() => {
    if (!isWarning) setWarningOpen(false)
  }, [isWarning])

  // ==========================================================================
  // SESSION-LOGOUT-WHILE-POLLING-01 / H1 -- a live background job is presence
  // ==========================================================================
  //
  // `useIdleTimer` counts DOM input and nothing else, which is correct until
  // the screen's whole purpose is to be watched. The QTO cockpit polls a
  // multi-hour take-off every 2 s; the user has no reason to move the mouse,
  // and after 15 minutes of not moving it the session was torn down mid-job.
  //
  // So a SECOND kind of evidence feeds the same timer, and it is deliberately
  // narrow -- all three of these must hold:
  //
  //   * the request was marked `background: true` (today: the cockpit's poll of
  //     a job THIS user enqueued),
  //   * it SUCCEEDED -- the job answering is the job still being alive,
  //   * the tab is VISIBLE.
  //
  // Visibility is the part that keeps this a security control rather than an
  // off switch. A forgotten tab polls exactly as eagerly as a watched one, so
  // without it any user who left the app open on this page would never time
  // out. Hidden tab -> the pings are ignored and the normal rules apply.
  //
  // Terminality needs no check either: the poll stops scheduling ticks the
  // moment the job reaches completed/failed/cancelled, so the pings simply stop
  // and the full idle window runs from the last one. The timeout still fires
  // for an abandoned desk -- just not while a job the user started is visibly
  // still running in front of them.
  const resetRef = useRef(reset)
  resetRef.current = reset   // `reset` is a fresh closure each render; the
                             // subscription below must not churn with it.

  useEffect(() => {
    if (!authed) return
    return subscribeBackgroundActivity(() => {
      // `visibilityState` is absent in non-browser contexts; treat that as
      // hidden so the narrow signal can never be the one that keeps a
      // server-rendered or test environment permanently alive.
      if (typeof document === 'undefined') return
      if (document.visibilityState !== 'visible') return
      resetRef.current()
    })
  }, [authed])

  return (
    <>
      {children}
      <ConnectionLostBanner />
      <IdleWarningModal
        isOpen={warningOpen && authed}
        msUntilIdle={msUntilIdle}
        onStay={() => {
          setWarningOpen(false)
          reset()
        }}
        onLogout={() => {
          setWarningOpen(false)
          try {
            clearAllAuth()
            publishAuthCleared()
            window.location.href = '/login'
          } catch {
            // no-op
          }
        }}
      />
    </>
  )
}
